import { Injectable } from '@angular/core';
import { Contacts} from '@ionic-native/contacts';
import { LoggerEvent, LoggerProvider } from '../logger/logger';
import { Platform } from 'ionic-angular';
import { StorageProvider } from '../storage/storage';
import { StorageType } from '../../model/types/storage-type';
import { RouteProvider } from '../route/route';
import { TranslateService } from '@ngx-translate/core';
import { APP_CONFIG, Env } from '../../environment';
import { HttpProvider } from '../http/http';

@Injectable()
export class ContactsProvider {
  reasons = {
    syncContacts: 'page.sendMoneyPaymentPage.recipientContactSyncBody',
    addMobileWallet: 'page.sendMoneyPaymentPage.contactSyncBody'
  }

  private customersIdsAutoSyncContactsArr=null;
  private deviceContacts = null;
  constructor(private platform: Platform,
              private translate: TranslateService,
              private storage: StorageProvider,
              private routeProvider: RouteProvider,
              private httpProvider: HttpProvider,
              private contacts: Contacts) {
  }

  // Whether the in-app contacts prompt for this reason was already accepted, without showing it
  async isPermissionAccepted(reason): Promise<boolean> {
    const reasonName = Object.keys(this.reasons).find((name) => this.reasons[name] === reason);
    return !!reasonName && !!(await this.storage.get(StorageType.ContactsPermission + '_' + reasonName));
  }

  checkPermission(reason) {
    LoggerProvider.info(this, 'Checking contacts permission', reason);

    let reasonName = '';
    for (const name in this.reasons) {
      if (this.reasons[name] === reason) {
        LoggerProvider.info(this, 'Found contacts reason ' + reasonName);
        reasonName = name;
        break;
      }
    }
    return new Promise(async(resolve) => {
      if (!reasonName) {
        LoggerProvider.error(this, LoggerEvent.ProviderException, 'Unknown reason to access contacts', reason);
        resolve(false);
        return;
      }

      const storageKey = StorageType.ContactsPermission + '_' + reasonName;
      const permission = await this.storage.get(storageKey);
      if (permission) {
        LoggerProvider.info(this, 'Contact permission ' + reasonName + ' is already accepted');
        resolve(true);
        return;
      }

      let resolveResult = false;
      const params = {
        title: this.translate.instant('page.eWalletPage.requestDetails.accessContactsHeader'),
        subtitle: this.translate.instant(reason),
        primaryButtonText : this.translate.instant('page.eWalletPage.requestDetails.allowButton'),
        secondaryButtonText:  this.translate.instant('page.eWalletPage.requestDetails.rejectButton'),
        primaryAction: () => {
          resolveResult = true;
          this.storage.set(storageKey, '1');
        },
        showSecondaryButton : true
      };
      this.routeProvider.openActionSheet(this.routeProvider.pages.ModalAlertPage, params, () => {
        if (resolveResult)
          LoggerProvider.info(this, 'Accepted contact permission ' + reasonName);
        else
          LoggerProvider.info(this, 'Rejected contact permission ' + reasonName);
        resolve(resolveResult);
      });
    });
  }

  /**
   * Read the stored in-app consent for a reason WITHOUT prompting. Used by the
   * Vue bridge `contacts-permission-check` to drive banner visibility.
   */
  async getConsentStatus(reason): Promise<'granted' | 'undetermined'> {
    let reasonName = '';
    for (const name in this.reasons) {
      if (this.reasons[name] === reason) {
        reasonName = name;
        break;
      }
    }
    if (!reasonName)
      return 'undetermined';
    const permission = await this.storage.get(StorageType.ContactsPermission + '_' + reasonName);
    return permission ? 'granted' : 'undetermined';
  }

  async pickContact(reason) {
    const result = await this.checkPermission(reason);
    if (!result)
      return;
    let contact = await this.contacts.pickContact();
    return contact;
  }

  findContacts(reason, search, options) {
    return new Promise(async(resolve, reject) => {
      const result = await this.checkPermission(reason);
      if (!result) {
        resolve(false);
        return;
      }

      if(!this.platform.is('cordova') && APP_CONFIG.env !== Env.PROD) {
        // to simulate contacts sync
        let contacts = [];
        while (contacts.length < 3000) {
          const list = this.getTestingP2PPhoneContacts();
          contacts = contacts.concat(list);
        }
        window.setTimeout(()=> {
          resolve(contacts);
        }, 1000);
      } else {
        this.contacts.find(search, options)
        .then((contactsList) => {
          resolve(contactsList);
        }).catch((error)=>{
          reject(error);
        })
      }
    });
  }

  async isAccessToContactsGiven():Promise <boolean>{
    const customersIds = await this.storage.get(StorageType.IsContactsAccessGiven);
    let isCurrentUserFound = false;

    this.customersIdsAutoSyncContactsArr = [];
    if(customersIds){
      const customerId = this.httpProvider.getAuthCustomerId();
      if (customerId) {
        this.customersIdsAutoSyncContactsArr = customersIds.split(',');

        isCurrentUserFound = this.customersIdsAutoSyncContactsArr.some( (id) =>
          id === customerId.toString()
        );
      }
    }
    return isCurrentUserFound;
  }

  /*
  * this function to update that sync contact called for one time after login
  */
  // syncContactInitCalled(){
  //   this.isSyncContactCalledOneTime = true ;
  //   return this.isSyncContactCalledOneTime ;
  // }
  async setAccessToContactAllowed(){
    const customerId = this.httpProvider.getAuthCustomerId();
    if (customerId) {
      if (!this.customersIdsAutoSyncContactsArr)
        await this.isAccessToContactsGiven();
      if (this.customersIdsAutoSyncContactsArr.indexOf(customerId.toString()) === -1) {
        this.customersIdsAutoSyncContactsArr.push(customerId.toString()); // the array init value at this provider is empty array
        this.storage.set(StorageType.IsContactsAccessGiven,this.customersIdsAutoSyncContactsArr.join());
      }
    }
  }

  async setAccessToContactsDenied(){
    if (!this.customersIdsAutoSyncContactsArr)
      await this.isAccessToContactsGiven();
    const id =  this.httpProvider.getAuthCustomerId();
    if(id){
      const index = this.customersIdsAutoSyncContactsArr.indexOf(id.toString());
      if(index > -1){
        this.customersIdsAutoSyncContactsArr.splice(index,1);
        this.storage.set(StorageType.IsContactsAccessGiven,this.customersIdsAutoSyncContactsArr.join());
      }
    }
  }

  setDeviceContacts(contacts){
    this.deviceContacts = contacts;
  }

  getDeviceContacts(){
    return this.deviceContacts;
  }

  getTestingP2PPhoneContacts(){
    return [{
      name: {
        givenName: 'AMY',
        familyName: 'Ztest_one',
      },
      phoneNumbers: [{
        type: 'mobile',
        value: '+2211770365428'
      }],
    },
    {
      name: {
        givenName: 'BMY',
        familyName: 'Ytest_two',
      },
      phoneNumbers: [{
        type: 'mobile',
        value: '+60124411600',
      }],
    },
    {
      name: {
        givenName: 'Non',
        familyName: 'Active',
      },
      phoneNumbers: [{
        type: 'landline',
        value: '+60173891114',
      }],
    },
    {
      name: {
        givenName: 'CMY user',
        familyName: 'Xthird',
      },
      phoneNumbers: [{
        type: 'mobile',
        value: '+60126655447',
      }],
    }];
  }
}



// WEBPACK FOOTER //
// ./src/providers/contacts/contacts.ts